import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBackOutlined, HomeOutlined } from '@mui/icons-material';
import LogoIcon from '../components/LogoIcon';
import { useThemeMode } from '../contexts/ThemeContext';

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const bg     = isDark ? '#0A0A0A' : '#F5F5F5';
  const card   = isDark ? '#111111' : '#FFFFFF';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const textP  = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS  = isDark ? '#888888' : '#666666';
  const textM  = isDark ? '#555555' : '#AAAAAA';

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: bg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>

        {/* Brand */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 8, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <LogoIcon size={20} letterColor={isDark ? '#EFEFEF' : '#FF1A50'} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: textP, letterSpacing: '-0.02em' }}>
            LLM Wrapper
          </Typography>
          <Box sx={{
            ml: 0.5, px: 1, py: 0.25, borderRadius: '4px',
            bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
            border: `1px solid ${border}`,
          }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, color: textM, letterSpacing: '0.04em' }}>
              ADMIN
            </Typography>
          </Box>
        </Box>

        {/* Error code */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#FF1A50', flexShrink: 0 }} />
          <Typography sx={{
            fontFamily: '"JetBrains Mono", "Courier New", monospace',
            fontSize: '0.7rem', fontWeight: 600,
            color: '#FF1A50', letterSpacing: '0.06em',
          }}>
            HTTP 404 · NOT_FOUND
          </Typography>
        </Box>

        {/* Large 404 */}
        <Typography sx={{
          fontFamily: '"JetBrains Mono", "Courier New", monospace',
          fontSize: '6rem',
          fontWeight: 700,
          color: 'transparent',
          WebkitTextStroke: isDark ? '1px rgba(255,255,255,0.12)' : '1px rgba(0,0,0,0.1)',
          letterSpacing: '-0.04em',
          lineHeight: 1,
          userSelect: 'none',
          mb: 1,
        }}>
          404
        </Typography>

        {/* Message */}
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: textP, letterSpacing: '-0.03em', lineHeight: 1.2, mb: 1 }}>
          Page not found
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: textS, mb: 5, lineHeight: 1.65 }}>
          The page you're looking for may have been moved, deleted, or never existed.
        </Typography>

        {/* Card with actions */}
        <Box sx={{
          bgcolor: card,
          border: `1px solid ${border}`,
          borderRadius: '10px',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}>
          <Button
            fullWidth
            startIcon={<HomeOutlined sx={{ fontSize: 15 }} />}
            onClick={() => navigate('/')}
            sx={{
              py: 1.25,
              fontSize: '0.875rem',
              fontWeight: 500,
              bgcolor: isDark ? '#EFEFEF' : '#0A0A0A',
              color: isDark ? '#0A0A0A' : '#EFEFEF',
              borderRadius: '7px',
              border: 'none',
              letterSpacing: '-0.01em',
              '&:hover': { bgcolor: isDark ? '#FFFFFF' : '#1A1A1A' },
              transition: 'background 0.15s ease',
              textTransform: 'none',
            }}
          >
            Go to Dashboard
          </Button>

          <Button
            fullWidth
            startIcon={<ArrowBackOutlined sx={{ fontSize: 15 }} />}
            onClick={() => navigate(-1 as any)}
            sx={{
              py: 1.1,
              fontSize: '0.8rem',
              fontWeight: 500,
              color: textS,
              borderRadius: '7px',
              border: `1px solid ${border}`,
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                color: textP,
              },
              transition: 'all 0.15s ease',
              textTransform: 'none',
            }}
          >
            Go Back
          </Button>
        </Box>

        {/* Footer */}
        <Typography sx={{ mt: 4, fontSize: '0.72rem', color: textM, textAlign: 'center', lineHeight: 1.6 }}>
          LLM Wrapper Admin Console · v2.0
        </Typography>
      </Box>
    </Box>
  );
};

export default NotFoundPage;
